import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
} from '@mui/material';
import apiClient from '@uniqcamp/shared/services/api.service';
import SchoolIcon from '@mui/icons-material/School';
import { GatesResponse } from '../types';

function SchoolCodeEntry({ onSuccess }: { onSuccess: (code: string, otp: string) => void }) {
  const [code, setCode] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) {
      setError('Please enter a school code');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const params = otp.trim() ? `?otp=${encodeURIComponent(otp.trim())}` : '';
      const res = await apiClient.get(`/tv-display/${encodeURIComponent(trimmed)}${params}`);
      const data = res.data as GatesResponse;
      if (data.success && data.gates?.length) {
        onSuccess(trimmed, otp.trim());
      } else if (data.success && (!data.gates || data.gates.length === 0)) {
        setError('No gates configured for this school. Contact the school admin.');
      } else {
        setError('School not found. Check the code and try again.');
      }
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number; data?: { message?: string; otpRequired?: boolean } } };
      if (ax.response?.status === 401 && ax.response?.data?.otpRequired) {
        setError('Invalid display OTP. Please get the current OTP from your school principal.');
      } else if (ax.response?.status === 404) {
        setError('School not found. Check the code and try again.');
      } else {
        setError(ax.response?.data?.message || 'Failed to load. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <Paper sx={{ p: 4, width: '100%' }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <SchoolIcon sx={{ fontSize: 56, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Display Panel
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Enter your school code and display OTP to continue
            </Typography>
          </Box>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="School code"
              value={code}
              onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(''); }}
              placeholder="e.g. ABC"
              error={!!error}
              disabled={loading}
              inputProps={{ maxLength: 20, style: { textTransform: 'uppercase', letterSpacing: '0.15em', fontSize: '1.25rem' } }}
              sx={{ mb: 2 }}
              autoFocus
            />
            <TextField
              fullWidth
              label="Display OTP"
              value={otp}
              onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
              placeholder="6-digit OTP from principal"
              error={!!error}
              helperText={error}
              disabled={loading}
              inputProps={{ maxLength: 6, inputMode: 'numeric', style: { letterSpacing: '0.3em', fontSize: '1.25rem', textAlign: 'center' } }}
              sx={{ mb: 2 }}
            />
            <Button type="submit" fullWidth variant="contained" size="large" disabled={loading}>
              {loading ? 'Checking…' : 'Continue'}
            </Button>
          </form>
        </Paper>
      </Box>
    </Container>
  );
}

export default SchoolCodeEntry;
